import { FormattedMessage } from "react-intl";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { useEffect, useState } from "react";
import NoResultsTemplate from "../common/NoResultsTemplate.tsx";
import styles from "./LibrarySearch.module.scss";
import { SearchOutletContext } from "../layout/outletContext.ts";
import PaddedDotLoader from "../common/PaddedDotLoader.tsx";
import { getSearchQuery } from "../query.ts";
import { HomeOutletContext } from "../../layout/useHomeOutletContext.ts";
import useContentResults from "../results/useContentResults.ts";
import useTitleResults from "../results/useTitleResults.ts";
import { deferFn } from "../../../../common/utils/function.ts";
import SearchAnnouncer from "./SearchAnnouncer.tsx";

function NoResults({ query }: { query: string }) {
  return (
    <NoResultsTemplate
      useStoreLink
      className={styles.noResults}
      title={
        <FormattedMessage
          defaultMessage="We couldn’t find a match for “{query}”"
          values={{ query }}
        />
      }
      message={
        <FormattedMessage defaultMessage="Double-check your spelling, or try using a different search term!" />
      }
    />
  );
}

export default function LibrarySearch() {
  const [urlParams] = useSearchParams();
  const query = getSearchQuery(urlParams);
  const { isSidebarExpandable, onSidebarToggle } =
    useOutletContext<HomeOutletContext>();
  const navigate = useNavigate();
  const location = useLocation();
  const titleResults = useTitleResults();
  const contentResults = useContentResults(urlParams.toString());
  // when no title results and switching to content, reduces flash of empty title results
  const [waitForTabFlip, setWaitForTabFlip] = useState(true);

  /**
   * Normal patterns suggest putting this in a loader function, but that doesn't
   * work here because some searches may be very slow and there are different
   * UI treatments for no results anywhere vs no results in a particular tab
   *
   * useFetchers in the children also wasn't ideal because they clear out
   * once the action completes and data has been revalidated
   *
   * Deferred loaders are also not ideal because it's one per route and iirc
   * doesn't refetch on query change
   *
   */

  // titles normally comes in first so prioritize that if it has results
  // if it comes in empty or if titles comes in second we need to wait
  // for both to land cleanly on the content tab if that has results and titles doesn't
  const isReady =
    titleResults.status === "has-results" ||
    (titleResults.status !== "loading" && contentResults.status !== "loading");
  const noResults =
    titleResults.status === "empty" && contentResults.status === "empty";

  useEffect(() => {
    // an empty titles mean we flip to the content tab
    if (
      isReady &&
      titleResults.status === "empty" &&
      contentResults.status === "has-results"
    ) {
      void navigate(`/search/library/book-content?${urlParams.toString()}`);
    } else if (isReady) {
      setWaitForTabFlip(false);
    }
  }, [
    contentResults.status,
    isReady,
    navigate,
    titleResults.status,
    urlParams,
  ]);

  useEffect(() => {
    if (waitForTabFlip && isReady) {
      if (location.pathname === "/search/library/book-content") {
        // used to prevent a micro flash of empty titles
        deferFn(() => setWaitForTabFlip(false));
      }
    }
  }, [location.pathname, isReady, waitForTabFlip]);

  // If no query, render the start screen via outlet (index route)
  if (!query) {
    return <Outlet context={{ isSidebarExpandable, onSidebarToggle }} />;
  }

  const renderResultsArea = () => {
    if (noResults) {
      return (
        <div className={styles.resultsArea}>
          <NoResults query={query} />
        </div>
      );
    }

    if (isReady) {
      return (
        <div>
          <div className={styles.tabRow}>
            <NavLink
              to={`/search/library/titles?${urlParams.toString()}`}
              className="vst-tab"
            >
              <FormattedMessage defaultMessage="Titles" />
            </NavLink>
            <NavLink
              to={`/search/library/book-content?${urlParams.toString()}`}
              className="vst-tab"
            >
              <FormattedMessage defaultMessage="Book Content" />
            </NavLink>
          </div>
          <Outlet
            context={
              {
                query,
                isSidebarExpandable,
                onSidebarToggle,
                titleResults,
                contentResults,
                waitForTabFlip,
              } satisfies SearchOutletContext & HomeOutletContext
            }
          />
        </div>
      );
    }

    return (
      <div className={styles.resultsArea}>
        <PaddedDotLoader aria-hidden={true} />
        <div className={styles.loadingTitle}>
          <FormattedMessage defaultMessage="Loading Results" />
        </div>
        <div className={styles.loadingMessage}>
          <FormattedMessage defaultMessage="This may take a few moments" />
        </div>
      </div>
    );
  };

  return (
    <>
      <SearchAnnouncer
        titleResults={titleResults}
        contentResults={contentResults}
        query={query}
        noResults={noResults}
        resultsReady={isReady}
        // noResults and resultsReady could be derived from titleResults and
        // contentResults, but it's more consistent to use the same variables
        // that are used for the results content area.
      />
      {renderResultsArea()}
    </>
  );
}
